import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Boxes, AlertOctagon, Camera, Plus } from "lucide-react";

import { useEvaluationCriteria } from "@/application/use-cases/products/use-criteria.queries";
import { useProducts } from "@/application/use-cases/products/use-products.queries";
import { useAllProductScores } from "@/application/use-cases/products/use-scores.queries";
import {
  useDefaultWeightsPreset,
  useWeightsPresets,
} from "@/application/use-cases/products/use-weights.queries";
import { useAllFeatureProducts } from "@/application/use-cases/products/use-feature-products.queries";
import { useFeaturesList } from "@/application/use-cases/features/use-features.queries";
import { useAllVerdicts } from "@/application/use-cases/feature-roadmap-verdicts/use-verdicts.queries";
import { computeProductScore } from "@/application/use-cases/products/compute-product-score";
import { ContributionBar } from "@/presentation/components/products/contribution-bar";
import { CreateProductDialog } from "@/presentation/components/products/create-product-dialog";
import { WeightsPresetControl } from "@/presentation/components/products/weights-preset-control";
import { PageExplainer } from "@/presentation/components/shell/page-explainer";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import { architectureComponents } from "@/presentation/data/architecture-components";
import { colorForGroup } from "@/presentation/data/criteria";
import { DOMAIN_COLOR } from "@/presentation/data/domains";
import type {
  EvaluationCriterionRow,
  FeatureProductRow,
  FeatureRoadmapVerdictRow,
  FeatureRow,
  ProductRow,
  ProductScoreRow,
} from "@/infrastructure/supabase/database.types";

export const Route = createFileRoute("/_app/products/")({
  component: ProductsPage,
});

const PRODUCT_KIND_LABEL = {
  "off-the-shelf": "Off-the-shelf",
  "build-on-top": "Build on top",
  "from-scratch": "From scratch",
} as const;

const PRODUCT_KIND_COLOR = {
  "off-the-shelf": "oklch(0.55 0.17 190)",
  "build-on-top": "oklch(0.62 0.16 50)",
  "from-scratch": "oklch(0.55 0.16 260)",
} as const;

interface Ranked {
  product: ProductRow;
  total: number;
  segments: { slug: string; label: string; color: string; value: number }[];
  dealbreakerFails: string[];
  scoredCount: number;
  evidencePct: number;
  vibesCount: number;
}

function ProductsPage() {
  const productsQ = useProducts();
  const criteriaQ = useEvaluationCriteria();
  const scoresQ = useAllProductScores();
  const presetQ = useDefaultWeightsPreset();
  const presetsQ = useWeightsPresets();
  const featureProductsQ = useAllFeatureProducts();
  const featuresQ = useFeaturesList();
  const verdictsQ = useAllVerdicts();
  const [snapshot, setSnapshot] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const products = productsQ.data ?? [];
  const criteria = criteriaQ.data ?? [];
  const scores = scoresQ.data ?? [];
  const preset = presetQ.data ?? null;
  const presets = presetsQ.data ?? [];
  const weights = preset?.weights ?? {};
  const featureProducts = featureProductsQ.data ?? [];
  const features = featuresQ.data ?? [];
  const verdicts = verdictsQ.data ?? [];

  const ranked = useMemo(
    () => rankProducts(products, criteria, scores, featureProducts, features, verdicts, weights),
    [products, criteria, scores, featureProducts, features, verdicts, weights]
  );

  const aboveLine = ranked.filter((r) => r.dealbreakerFails.length === 0);
  const belowLine = ranked.filter((r) => r.dealbreakerFails.length > 0);
  const totalDealbreakers = belowLine.reduce((acc, r) => acc + r.dealbreakerFails.length, 0);
  const totalEvidenceCovered = aboveLine.reduce((acc, r) => acc + r.evidencePct, 0);
  const avgEvidencePct =
    aboveLine.length > 0 ? Math.round(totalEvidenceCovered / aboveLine.length) : 0;

  const isLoading =
    productsQ.isLoading ||
    criteriaQ.isLoading ||
    scoresQ.isLoading ||
    presetQ.isLoading ||
    presetsQ.isLoading ||
    featureProductsQ.isLoading ||
    featuresQ.isLoading ||
    verdictsQ.isLoading;

  if (isLoading) {
    return (
      <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:p-10">
        <div className="bg-muted/40 h-48 animate-pulse rounded-2xl" />
        <div className="bg-muted/30 h-64 animate-pulse rounded-2xl" />
      </div>
    );
  }

  return (
    <div
      className={`animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10 ${snapshot ? "products-snapshot" : ""}`}
      data-snapshot={snapshot ? "true" : "false"}
    >
      <div className="self-end print:hidden">
        <PageExplainer pageKey="products" />
      </div>
      <RouteHero
        eyebrow="Evaluation"
        icon={Boxes}
        numeral={String(aboveLine.length).padStart(2, "0")}
        numeralTone="cyan"
        numeralSuffix={`/ ${String(products.length).padStart(2, "0")} ranked`}
        title="Product shelves, weighed and ranked"
        summary="Each product scored across 16 weighted criteria. Dealbreaker fails drop a product below the line regardless of total. Use the snapshot toggle to capture a clean view for the owner deck."
        meta={[
          { label: "Above line", value: aboveLine.length, tone: "cyan" },
          {
            label: "Below line",
            value: belowLine.length,
            tone: belowLine.length > 0 ? "red" : "default",
          },
          {
            label: "Dealbreakers",
            value: totalDealbreakers,
            tone: totalDealbreakers > 0 ? "red" : "default",
          },
          {
            label: "Avg evidence",
            value: `${avgEvidencePct}%`,
            tone: avgEvidencePct >= 70 ? "green" : "amber",
          },
        ]}
        actions={
          <div className="flex flex-col gap-2 print:hidden">
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex w-fit items-center gap-1.5 rounded-md border border-[oklch(0.72_0.17_190_/_0.40)] bg-[oklch(0.72_0.17_190_/_0.20)] px-3 py-1.5 text-[11.5px] font-medium text-[oklch(0.92_0.07_190)] transition-colors hover:bg-[oklch(0.72_0.17_190_/_0.30)]"
            >
              <Plus className="size-3" />
              New product
            </button>
            <button
              type="button"
              onClick={() => setSnapshot((v) => !v)}
              className="inline-flex w-fit items-center gap-1.5 rounded-md border border-[oklch(1_0_0_/_0.18)] bg-[oklch(1_0_0_/_0.04)] px-3 py-1.5 text-[11.5px] font-medium text-[oklch(0.97_0.005_240)] transition-colors hover:bg-[oklch(1_0_0_/_0.08)]"
            >
              <Camera className="size-3" />
              {snapshot ? "Exit snapshot view" : "Snapshot view"}
            </button>
          </div>
        }
      />

      <CreateProductDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)] lg:gap-8 print:grid-cols-1">
        <div className="print:hidden">
          <WeightsPresetControl criteria={criteria} presets={presets} active={preset} />
        </div>

        <div className="flex flex-col gap-3">
          <div className="text-muted-foreground flex items-center justify-between text-[10.5px] font-medium tracking-[0.12em] uppercase">
            <span>Ranked</span>
            <span>Weighted total / 5.00</span>
          </div>

          {aboveLine.length === 0 ? (
            <EmptyAboveLine />
          ) : (
            <ul className="flex flex-col gap-3">
              {aboveLine.map((r, idx) => (
                <li key={r.product.id}>
                  <RankedRow rank={idx + 1} entry={r} />
                </li>
              ))}
            </ul>
          )}

          {belowLine.length > 0 ? (
            <div className="mt-6 flex flex-col gap-3">
              <div
                className="flex items-center gap-2 text-[10.5px] font-medium tracking-[0.12em] uppercase"
                style={{ color: "oklch(0.55 0.20 25)" }}
              >
                <AlertOctagon className="size-3" />
                <span>Below the line — dealbreaker fails</span>
              </div>
              <ul className="flex flex-col gap-3">
                {belowLine.map((r) => (
                  <li key={r.product.id}>
                    <BelowLineRow entry={r} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function RankedRow({ rank, entry }: { rank: number; entry: Ranked }) {
  const accent = PRODUCT_KIND_COLOR[entry.product.kind];
  return (
    <Link
      to="/products/$slug"
      params={{ slug: entry.product.slug }}
      className="glow-card group relative flex flex-col gap-3 overflow-hidden p-5 transition-colors print:break-inside-avoid"
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex items-baseline gap-3">
          <span className="font-data text-foreground/40 text-[22px] tabular-nums">
            {String(rank).padStart(2, "0")}
          </span>
          <div className="flex flex-col gap-0.5">
            <h3 className="text-[15px] leading-tight font-semibold">{entry.product.name}</h3>
            <span className="text-foreground/60 text-[11.5px]">
              {PRODUCT_KIND_LABEL[entry.product.kind]}
              {entry.product.vendor ? ` · ${entry.product.vendor}` : ""}
            </span>
          </div>
        </div>
        <span className="font-data text-[22px] font-medium tabular-nums" style={{ color: accent }}>
          {entry.total.toFixed(2)}
        </span>
      </div>

      <ContributionBar segments={entry.segments} maxTotal={5} height={12} />

      <ComponentChips componentIds={entry.product.applies_to_components} />

      <div className="text-foreground/55 flex flex-wrap items-center gap-3 text-[10.5px]">
        <span>
          {entry.scoredCount} criteria scored · {entry.evidencePct}% with evidence
        </span>
        {entry.vibesCount > 0 ? (
          <span style={{ color: "oklch(0.55 0.16 80)" }}>{entry.vibesCount} vibes-flagged</span>
        ) : null}
      </div>
    </Link>
  );
}

function BelowLineRow({ entry }: { entry: Ranked }) {
  return (
    <Link
      to="/products/$slug"
      params={{ slug: entry.product.slug }}
      className="glow-card flex flex-col gap-2 p-4 transition-colors print:break-inside-avoid"
      style={{ borderLeft: "3px solid oklch(0.62 0.18 25)" }}
    >
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="text-[14px] leading-tight font-semibold line-through decoration-[oklch(0.62_0.18_25)] decoration-2">
          {entry.product.name}
        </h3>
        <span className="font-data text-foreground/40 text-[14px] tabular-nums">
          {entry.total.toFixed(2)}
        </span>
      </div>
      <ul className="flex flex-col gap-0.5 text-[11.5px]" style={{ color: "oklch(0.45 0.18 25)" }}>
        {entry.dealbreakerFails.map((label) => (
          <li key={label}>· fails: {label}</li>
        ))}
      </ul>
      <ComponentChips componentIds={entry.product.applies_to_components} />
    </Link>
  );
}

function ComponentChips({ componentIds }: { componentIds: string[] | null }) {
  if (!componentIds || componentIds.length === 0) return null;
  const resolved = componentIds
    .map((id) => architectureComponents.find((c) => c.id === id))
    .filter((c): c is (typeof architectureComponents)[number] => Boolean(c));
  if (resolved.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {resolved.map((c) => {
        const color = DOMAIN_COLOR[c.domain];
        return (
          <span
            key={c.id}
            className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] leading-none"
            style={{
              borderColor: `color-mix(in oklch, ${color} 40%, transparent)`,
              backgroundColor: `color-mix(in oklch, ${color} 10%, transparent)`,
              color: `color-mix(in oklch, ${color} 80%, white)`,
            }}
            title={c.role}
          >
            {c.name}
          </span>
        );
      })}
    </div>
  );
}

function EmptyAboveLine() {
  return (
    <div className="glow-card flex flex-col gap-2 p-8 text-center">
      <span className="text-foreground/70 text-sm">No products evaluated yet.</span>
      <span className="text-muted-foreground text-[12px]">
        Seed product rows via migration, or add them through the Supabase Studio table editor.
      </span>
    </div>
  );
}

function rankProducts(
  products: ProductRow[],
  criteria: EvaluationCriterionRow[],
  scores: ProductScoreRow[],
  featureProducts: FeatureProductRow[],
  features: FeatureRow[],
  verdicts: FeatureRoadmapVerdictRow[],
  weights: Record<string, number>
): Ranked[] {
  const scoresByProduct = new Map<string, ProductScoreRow[]>();
  for (const s of scores) {
    const bucket = scoresByProduct.get(s.product_id) ?? [];
    bucket.push(s);
    scoresByProduct.set(s.product_id, bucket);
  }
  const fpByProduct = new Map<string, FeatureProductRow[]>();
  for (const fp of featureProducts) {
    const bucket = fpByProduct.get(fp.product_id) ?? [];
    bucket.push(fp);
    fpByProduct.set(fp.product_id, bucket);
  }
  // Evidence % is measured against the manual rubric (derived criteria carry no
  // hand-entered evidence) — same denominator as the detail page.
  const manualCount = criteria.filter((c) => c.derivation_kind === "manual").length;

  const ranked = products.map((product) => {
    // Shared scorer — includes derived-coverage criteria, so this total matches
    // the product's detail page exactly. (Previously this loop iterated only
    // stored product_scores and silently dropped derived coverage.)
    const result = computeProductScore(
      scoresByProduct.get(product.id) ?? [],
      fpByProduct.get(product.id) ?? [],
      { criteria, features, verdicts, weights }
    );

    const segments = result.contributions
      .map((c) => ({
        slug: c.slug,
        label: c.label,
        color: colorForGroup(c.groupKey),
        value: c.value,
      }))
      .sort((a, b) => b.value - a.value);

    return {
      product,
      total: result.weightedTotal,
      segments,
      dealbreakerFails: result.dealbreakerNames,
      scoredCount: result.scoredCount,
      evidencePct: manualCount > 0 ? Math.round((result.withEvidenceCount / manualCount) * 100) : 0,
      vibesCount: result.vibesCount,
    } satisfies Ranked;
  });

  return ranked.sort((a, b) => {
    const aBelow = a.dealbreakerFails.length > 0;
    const bBelow = b.dealbreakerFails.length > 0;
    if (aBelow !== bBelow) return aBelow ? 1 : -1;
    return b.total - a.total;
  });
}
